/**
 * PRK TEX - Premium Luxury Shopify JS Engine
 * Vanilla ES6, No jQuery, WCAG-Compliant Keyboard Trapping & Accessibility
 */

window.theme = window.theme || {};

// 1. Drawer Module (Reusable for Cart, Navigation, and Filters)
class Drawer {
  constructor(id) {
    this.drawerEl = document.getElementById(id);
    if (!this.drawerEl) return;
    
    this.innerEl = this.drawerEl.querySelector('.drawer__inner');
    this.closeBtn = this.drawerEl.querySelector('.drawer__close-btn');
    this.isOpen = false;
    
    // Bind events
    this.drawerEl.addEventListener('keydown', (e) => this._handleKeyDown(e));
  }

  open() {
    if (this.isOpen) return;
    this.drawerEl.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden'; // Stop background scrolling
    this.isOpen = true;
    
    // Focus close button
    setTimeout(() => {
      if (this.closeBtn) this.closeBtn.focus();
    }, 100);
    
    // Dispatches custom event
    const event = new CustomEvent('drawer:open', { detail: { id: this.drawerEl.id } });
    window.dispatchEvent(event);
  }

  close() {
    if (!this.isOpen) return;
    this.drawerEl.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.isOpen = false;
    
    const event = new CustomEvent('drawer:close', { detail: { id: this.drawerEl.id } });
    window.dispatchEvent(event);
  }

  _handleKeyDown(e) {
    if (e.key === 'Escape') {
      this.close();
    }
  }
}

// Instantiate Global Drawers
document.addEventListener('DOMContentLoaded', () => {
  theme.cartDrawer = new Drawer('CartDrawer');
  theme.initAccordions();
  theme.initSlideshows();
  theme.initQuantitySelectors();
  theme.initGalleries();
  theme.initStickyHeader();
  theme.initPredictiveSearch();
  theme.initRotatingAnnouncements();
  theme.initVoiceSearch();
  theme.initWishlistAndCompare();
  theme.initQuickView();
  theme.initProductAdvancedFeatures();
  theme.initCollectionLayout();
  theme.initLivePurchasesAlert();
  theme.initBackToTop();
  theme.initAccountDashboard();
  theme.initStoreLocator();
  theme.initPushNotifications();
  theme.initWishlistPage();
  theme.initComparePage();
});

// 2. Accordions Tabs (Product descriptions, sizing charts, etc.)
theme.initAccordions = () => {
  const accordionHeaders = document.querySelectorAll('.accordion__header');
  accordionHeaders.forEach(header => {
    header.addEventListener('click', () => {
      const item = header.parentElement;
      const content = item.querySelector('.accordion__content');
      
      if (item.classList.contains('is-open')) {
        item.classList.remove('is-open');
        content.style.maxHeight = null;
      } else {
        // Close other sibling items in same accordion wrapper
        const siblings = item.parentElement.querySelectorAll('.accordion__item');
        siblings.forEach(sib => {
          sib.classList.remove('is-open');
          const sibContent = sib.querySelector('.accordion__content');
          if (sibContent) sibContent.style.maxHeight = null;
        });

        item.classList.add('is-open');
        content.style.maxHeight = content.scrollHeight + 'px';
      }
    });
  });
};

// 3. Editorial Hero Slideshow Banners
theme.initSlideshows = () => {
  const slideshows = document.querySelectorAll('.slideshow');
  slideshows.forEach(slideshow => {
    const slides = slideshow.querySelectorAll('.slideshow__slide');
    if (slides.length <= 1) return;

    let currentIndex = 0;
    const intervalTime = 6000; // 6 seconds auto-rotate

    const nextSlide = () => {
      slides[currentIndex].classList.remove('is-active');
      currentIndex = (currentIndex + 1) % slides.length;
      slides[currentIndex].classList.add('is-active');
    };

    setInterval(nextSlide, intervalTime);
  });
};

// 4. Quantity Adjuster UI Components
theme.initQuantitySelectors = () => {
  document.body.addEventListener('click', (e) => {
    const btn = e.target.closest('.qty-btn');
    if (!btn) return;

    // Prevent navigation for anchor tags or default button submits
    e.preventDefault();

    const container = btn.closest('.qty-selector');
    if (!container) return;
    const input = container.querySelector('.qty-input');
    if (!input) return;

    const isPlus = btn.classList.contains('qty-btn--plus') || btn.textContent.trim() === '+';
    const isMinus = btn.classList.contains('qty-btn--minus') || btn.textContent.trim() === '-';

    let val = parseInt(input.value, 10) || 1;
    const minVal = parseInt(input.getAttribute('min'), 10) || 1;

    if (isPlus) {
      val++;
    } else if (isMinus) {
      val = val > minVal ? val - 1 : minVal;
    }

    if (val !== parseInt(input.value, 10)) {
      input.value = val;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  // Handle manual/programmatic input changes
  document.body.addEventListener('change', (e) => {
    if (e.target.matches('.qty-input')) {
      const input = e.target;
      let val = parseInt(input.value, 10);
      if (isNaN(val) || val < 1) {
        val = 1;
        input.value = 1;
      }

      const cartRow = input.closest('[data-cart-item-key]') || input.closest('.cart-drawer-item');
      if (cartRow) {
        const itemKey = cartRow.getAttribute('data-cart-item-key') || input.getAttribute('data-key');
        if (itemKey && theme.cart) {
          if (input.closest('#CartDrawerContent')) {
            theme.cart.changeQuantity(itemKey, val);
          } else if (window.location.pathname.includes('/cart')) {
            theme.cart.updatePageCart(itemKey, val);
          }
        }
      }
    }
  });
};

// 5. Product Image Gallery switching
theme.initGalleries = () => {
  const galleryThumbnails = document.querySelectorAll('.product-gallery__thumb');
  const mainImage = document.getElementById('MainProductImage');
  
  if (!mainImage) return;

  galleryThumbnails.forEach(thumb => {
    thumb.addEventListener('click', (e) => {
      e.preventDefault();
      const largeUrl = thumb.getAttribute('data-large-url');
      if (largeUrl) {
        mainImage.src = largeUrl;
      }
      
      // Active states styling
      galleryThumbnails.forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
    });
  });
};

// 6. Shopify AJAX Cart API Handlers
theme.cart = {
  getCart() {
    return fetch('/cart.js')
      .then(response => response.json());
  },

  addItem(id, quantity = 1) {
    const data = {
      items: [{
        id: id,
        quantity: quantity
      }]
    };

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
    .then(response => response.json())
    .then(item => {
      // Reload cart drawer and open it
      this.updateDrawer();
      theme.cartDrawer.open();
    })
    .catch(error => {
      console.error('Error adding product to cart:', error);
    });
  },

  updateDrawer() {
    const drawerContent = document.getElementById('CartDrawerContent');
    if (!drawerContent) return;

    drawerContent.innerHTML = '<div class="loading-spinner"></div>';

    this.getCart().then(cart => {
      if (cart.item_count === 0) {
        drawerContent.innerHTML = `
          <div class="cart-drawer-empty">
            <p>${window.theme.translations?.cartEmpty || 'Your cart is currently empty.'}</p>
            <a href="/collections/all" class="btn btn--primary btn--full" style="margin-top: 20px;">
              ${window.theme.translations?.continueShopping || 'Continue Shopping'}
            </a>
          </div>
        `;
        // Update header bubbles
        theme.updateHeaderBubbles(0);
        return;
      }

      theme.updateHeaderBubbles(cart.item_count);

      // 1. Free Shipping Progress Bar
      const threshold = 149900; // in cents (₹1,499)
      const total = cart.total_price;
      let progressHtml = '';
      if (total >= threshold) {
        progressHtml = `
          <div class="free-shipping-progress">
            <span style="font-family:var(--font-button); font-size:11px; font-weight:600; color:var(--color-primary);">🎉 Congratulations! You have unlocked Free Shipping!</span>
            <div class="free-shipping-progress__bar-track"><div class="free-shipping-progress__bar-fill" style="width: 100%;"></div></div>
          </div>
        `;
      } else {
        const remaining = threshold - total;
        const percent = (total / threshold) * 100;
        progressHtml = `
          <div class="free-shipping-progress">
            <span style="font-family:var(--font-button); font-size:11px; color:var(--color-body);">Add <strong>${theme.formatMoney(remaining)}</strong> more for <strong>FREE SHIPPING</strong>!</span>
            <div class="free-shipping-progress__bar-track"><div class="free-shipping-progress__bar-fill" style="width: ${percent}%;"></div></div>
          </div>
        `;
      }

      let itemsHtml = '';
      cart.items.forEach(item => {
        const itemPrice = theme.formatMoney(item.final_line_price);
        const optionsHtml = item.options_with_values.map(opt => `<span>${opt.name}: ${opt.value}</span>`).join(' | ');

        itemsHtml += `
          <div class="cart-drawer-item" data-cart-item-key="${item.key}" style="display: flex; gap: 15px; margin-bottom: 20px; border-bottom: 1px solid var(--color-border); padding-bottom: 15px;">
            <img src="${item.image}" alt="${item.title}" style="width: 70px; height: 90px; object-fit: cover; border-radius: 8px;">
            <div style="flex: 1;">
              <h4 class="font-heading" style="font-size: 16px; margin-bottom: 4px;">
                <a href="${item.url}">${item.product_title}</a>
              </h4>
              <p style="font-size: 12px; color: var(--color-secondary); margin-bottom: 8px;">${optionsHtml}</p>
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <div class="qty-selector" style="margin-bottom: 0;">
                  <button type="button" class="qty-btn qty-btn--minus" data-key="${item.key}">-</button>
                  <input type="number" class="qty-input" value="${item.quantity}" min="1" data-key="${item.key}">
                  <button type="button" class="qty-btn qty-btn--plus" data-key="${item.key}">+</button>
                </div>
                <span class="font-heading" style="font-weight: 600; font-size: 14px;">${itemPrice}</span>
              </div>
              <button type="button" class="btn-remove" onclick="theme.cart.changeQuantity('${item.key}', 0)" style="background: none; border: none; font-size: 11px; text-transform: uppercase; color: var(--color-accent); cursor: pointer; margin-top: 8px; padding: 0;">
                Remove
              </button>
            </div>
          </div>
        `;
      });

      const totalFormatted = theme.formatMoney(cart.total_price);

      drawerContent.innerHTML = `
        ${progressHtml}
        <div class="cart-drawer-items" style="max-height: 50vh; overflow-y: auto;">
          ${itemsHtml}
        </div>

        <div class="cart-drawer-footer" style="margin-top: 20px; border-top: 1px solid var(--color-border); padding-top: 20px;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 20px; font-family: var(--font-button); font-weight: 500;">
            <span>Subtotal</span>
            <span id="CartDrawerSubtotal">${totalFormatted}</span>
          </div>
          <p style="font-size: 12px; margin-bottom: 20px; color: var(--color-secondary);">Taxes and shipping calculated at checkout</p>
          <a href="/checkout" class="btn btn--primary btn--full">Checkout</a>
          <a href="/cart" class="btn btn--secondary btn--full" style="margin-top: 10px;">View Full Cart</a>
        </div>
      `;
    });
  },

  changeQuantity(key, newQty) {
    const data = {
      id: String(key),
      quantity: newQty
    };

    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
    .then(response => response.json())
    .then(cart => {
      this.updateDrawer();
    })
    .catch(error => {
      console.error('Error modifying quantity:', error);
    });
  },

  updatePageCart(key, newQty) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id: String(key),
        quantity: newQty
      })
    })
    .then(res => res.json())
    .then(cart => {
      window.location.reload();
    })
    .catch(err => {
      console.error('Error updating cart page:', err);
      window.location.reload();
    });
  }
};

// 7. Format Shopify Money Helper Function
theme.formatMoney = (cents) => {
  // Safe default INR representation (₹)
  const currencySymbol = '₹';
  const val = (cents / 100).toFixed(2);
  // Add commas for Indian currency formatting or standard thousands separator
  const parts = val.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return currencySymbol + ' ' + parts.join('.');
};

// 8. Update Cart Count Bubbles in UI
theme.updateHeaderBubbles = (count) => {
  const bubbles = document.querySelectorAll('.cart-count-bubble');
  bubbles.forEach(bubble => {
    bubble.textContent = count;
    if (count === 0) {
      bubble.style.display = 'none';
    } else {
      bubble.style.display = 'flex';
    }
  });
};

// 9. Sticky Header Shrink Trigger
theme.initStickyHeader = () => {
  // Main header sticky disabled - only search bar remains sticky on scroll
};

// 10. Multi-message Announcement Slider Rotating
theme.initRotatingAnnouncements = () => {
  const container = document.querySelector('.announcement-bar__slider');
  if (!container) return;
  const messages = container.querySelectorAll('.announcement-bar__message');
  if (messages.length <= 1) return;

  let currentIndex = 0;
  messages.forEach((msg, idx) => {
    msg.style.display = idx === 0 ? 'block' : 'none';
  });

  setInterval(() => {
    messages[currentIndex].style.display = 'none';
    currentIndex = (currentIndex + 1) % messages.length;
    messages[currentIndex].style.display = 'block';
  }, 4000); // Rotate every 4 seconds
};

// 11. Predictive Search Autocomplete suggest
theme.initPredictiveSearch = () => {
  const searchInputs = document.querySelectorAll('.predictive-search-input');
  searchInputs.forEach(input => {
    const container = input.closest('.header__search-container');
    if (!container) return;
    const resultsPanel = container.querySelector('.predictive-search');
    if (!resultsPanel) return;

    input.addEventListener('input', () => {
      const query = input.value.trim().toLowerCase();
      if (query.length < 2) {
        resultsPanel.style.display = 'none';
        return;
      }

      // Fetch from Shopify Predictive Search API
      fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=4`)
        .then(response => {
          if (!response.ok) throw new Error('API Unavailable');
          return response.json();
        })
        .then(data => {
          const products = data.resources.results.products;
          if (products.length === 0) {
            resultsPanel.innerHTML = '<div style="padding: 15px; text-align: center; font-size: 13px;">No suggestions found</div>';
          } else {
            let html = '<div class="predictive-search__section-title">SUGGESTED PRODUCTS</div>';
            products.forEach(prod => {
              html += `
                <a href="${prod.url}" class="predictive-search-item">
                  <img src="${prod.image}" alt="${prod.title}" class="predictive-search-item__image">
                  <div>
                    <div class="predictive-search-item__title">${prod.title}</div>
                    <div class="predictive-search-item__price">${theme.formatMoney(prod.price * 100)}</div>
                  </div>
                </a>
              `;
            });
            resultsPanel.innerHTML = html;
          }
          resultsPanel.style.display = 'block';
        })
        .catch(() => {
          resultsPanel.innerHTML = '<div style="padding: 15px; text-align: center; font-size: 13px;">No suggestions found</div>';
          resultsPanel.style.display = 'block';
        });
    });

    // Hide panel on blur
    document.addEventListener('click', (e) => {
      if (!container.contains(e.target)) {
        resultsPanel.style.display = 'none';
      }
    });
  });
};

// 12. Real Device Voice Search (HTML5 Web Speech API)
theme.startVoiceSearch = () => {
  let modal = document.getElementById('VoiceSearchModal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'VoiceSearchModal';
    modal.className = 'voice-search-modal';
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = `
      <div class="voice-search-modal__box" style="position: relative;">
        <button type="button" class="voice-search-modal__close" onclick="theme.stopVoiceSearch()" aria-label="Close Voice Search">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
        <div class="voice-search-modal__pulse" id="VoicePulseIcon">
          <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
            <path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8"/>
          </svg>
        </div>
        <h3 class="font-heading" style="font-size: 24px; margin-bottom: 8px;" id="VoiceTitle">Listening...</h3>
        <p style="color: var(--color-body); font-size: 14px; min-height: 24px;" id="VoiceStatus">Say something like "Banarasi Silk Saree"</p>
        <div style="margin-top: 20px;">
          <button type="button" class="btn btn--secondary" onclick="theme.stopVoiceSearch()" style="padding: 8px 20px; font-size: 12px;">Cancel</button>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
  }

  const statusEl = modal.querySelector('#VoiceStatus');
  const titleEl = modal.querySelector('#VoiceTitle');
  const pulseIcon = modal.querySelector('#VoicePulseIcon');
  modal.setAttribute('aria-hidden', 'false');

  const searchInput = document.querySelector('.predictive-search-input') || document.getElementById('FlipkartSearchInput');

  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

  if (SpeechRecognition) {
    if (window.themeSpeechRecognition) {
      try { window.themeSpeechRecognition.abort(); } catch(e){}
    }

    const recognition = new SpeechRecognition();
    window.themeSpeechRecognition = recognition;
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-IN';

    titleEl.textContent = 'Listening...';
    statusEl.textContent = 'Speak now into your microphone...';
    pulseIcon.classList.add('is-listening');

    recognition.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; ++i) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript;
        } else {
          interimTranscript += event.results[i][0].transcript;
        }
      }

      const transcript = finalTranscript || interimTranscript;
      if (transcript) {
        statusEl.textContent = `"${transcript}"`;
        if (searchInput) {
          searchInput.value = transcript;
          searchInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    };

    recognition.onerror = (event) => {
      console.warn('Speech recognition error:', event.error);
      pulseIcon.classList.remove('is-listening');
      if (event.error === 'not-allowed') {
        titleEl.textContent = 'Microphone Access Denied';
        statusEl.textContent = 'Please allow microphone permissions in browser settings.';
      } else if (event.error === 'no-speech') {
        titleEl.textContent = 'No Voice Heard';
        statusEl.textContent = 'Didn\'t catch that. Please try clicking the microphone again.';
      } else {
        titleEl.textContent = 'Voice Search';
        statusEl.textContent = 'Error: ' + event.error;
      }
    };

    recognition.onend = () => {
      pulseIcon.classList.remove('is-listening');
      if (searchInput && searchInput.value.trim().length > 0) {
        titleEl.textContent = 'Searching...';
        setTimeout(() => {
          theme.stopVoiceSearch();
          const form = searchInput.closest('form');
          if (form) form.submit();
        }, 800);
      } else {
        setTimeout(() => {
          theme.stopVoiceSearch();
        }, 2000);
      }
    };

    try {
      recognition.start();
    } catch(err) {
      console.error('Failed to start speech recognition:', err);
    }
  } else {
    titleEl.textContent = 'Voice Search Unsupported';
    statusEl.textContent = 'Your browser does not support live speech recognition. Please type query directly.';
    pulseIcon.classList.remove('is-listening');
  }
};

theme.stopVoiceSearch = () => {
  const modal = document.getElementById('VoiceSearchModal');
  if (modal) modal.setAttribute('aria-hidden', 'true');
  if (window.themeSpeechRecognition) {
    try { window.themeSpeechRecognition.stop(); } catch(e){}
  }
};
theme.initVoiceSearch = () => {
  const voiceBtns = document.querySelectorAll('.search-voice-btn, [data-voice-search]');
  voiceBtns.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      theme.startVoiceSearch();
    });
  });
};

// 13. LocalStorage Wishlist & Compare Manager


theme.initWishlistAndCompare = () => {
  const getList = (key) => JSON.parse(localStorage.getItem(key)) || [];
  const setList = (key, val) => localStorage.setItem(key, JSON.stringify(val));

  const updateButtonsState = () => {
    const wishlist = getList('wishlist');
    const compare = getList('compare');

    document.querySelectorAll('.btn-wishlist').forEach(btn => {
      const handle = btn.getAttribute('data-product-handle');
      if (wishlist.includes(handle)) {
        btn.classList.add('is-active');
        btn.querySelector('svg').setAttribute('fill', '#E03E3E');
      } else {
        btn.classList.remove('is-active');
        btn.querySelector('svg').setAttribute('fill', 'none');
      }
    });

    document.querySelectorAll('.btn-compare').forEach(btn => {
      const handle = btn.getAttribute('data-product-handle');
      if (compare.includes(handle)) {
        btn.classList.add('is-active');
        btn.style.color = 'var(--color-primary)';
      } else {
        btn.classList.remove('is-active');
        btn.style.color = '';
      }
    });
  };

  document.body.addEventListener('click', (e) => {
    const wishlistBtn = e.target.closest('.btn-wishlist');
    const compareBtn = e.target.closest('.btn-compare');

    if (wishlistBtn) {
      e.preventDefault();
      const handle = wishlistBtn.getAttribute('data-product-handle');
      let list = getList('wishlist');
      if (list.includes(handle)) {
        list = list.filter(item => item !== handle);
        theme.showFlashMessage('Removed from Wishlist');
      } else {
        list.push(handle);
        theme.showFlashMessage('Added to Wishlist!');
      }
      setList('wishlist', list);
      updateButtonsState();
    }

    if (compareBtn) {
      e.preventDefault();
      const handle = compareBtn.getAttribute('data-product-handle');
      let list = getList('compare');
      if (list.includes(handle)) {
        list = list.filter(item => item !== handle);
        theme.showFlashMessage('Removed from Compare list');
      } else {
        if (list.length >= 3) {
          theme.showFlashMessage('You can compare up to 3 products only.');
          return;
        }
        list.push(handle);
        theme.showFlashMessage('Added to Compare list!');
      }
      setList('compare', list);
      updateButtonsState();
    }
  });

  updateButtonsState();
};

theme.showFlashMessage = (msg) => {
  let flash = document.getElementById('ThemeFlashMessage');
  if (!flash) {
    flash = document.createElement('div');
    flash.id = 'ThemeFlashMessage';
    flash.style.position = 'fixed';
    flash.style.bottom = '30px';
    flash.style.right = '30px';
    flash.style.backgroundColor = 'var(--color-heading)';
    flash.style.color = '#FAF7F2';
    flash.style.padding = '12px 24px';
    flash.style.borderRadius = '8px';
    flash.style.zIndex = '9999';
    flash.style.fontSize = '13px';
    flash.style.fontFamily = 'var(--font-button)';
    flash.style.opacity = '0';
    flash.style.transform = 'translateY(10px)';
    flash.style.transition = 'all 0.3s ease';
    document.body.appendChild(flash);
  }
  flash.textContent = msg;
  flash.style.opacity = '1';
  flash.style.transform = 'translateY(0)';
  setTimeout(() => {
    flash.style.opacity = '0';
    flash.style.transform = 'translateY(10px)';
  }, 2500);
};

// 14. Quick View Popup Modal
theme.initQuickView = () => {
  // Create Quick View Modal dynamically if not present
  let modal = document.getElementById('QuickViewModal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'QuickViewModal';
    modal.className = 'quickview-modal';
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = `
      <div class="quickview-modal__box">
        <button type="button" class="quickview-modal__close" onclick="document.getElementById('QuickViewModal').setAttribute('aria-hidden', 'true')">
          <svg fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
        <div id="QuickViewModalContent"></div>
      </div>
    `;
    document.body.appendChild(modal);
  }

  document.body.addEventListener('click', (e) => {
    const qvBtn = e.target.closest('.btn-quickview');
    if (qvBtn) {
      e.preventDefault();
      modal.setAttribute('aria-hidden', 'false');
      const content = modal.querySelector('#QuickViewModalContent');
      content.innerHTML = '<div class="loading-spinner"></div>';
      
      const title = qvBtn.getAttribute('data-title') || 'Handwoven Silk Saree';
      const price = qvBtn.getAttribute('data-price') || '₹ 8,990.00';
      const image = qvBtn.getAttribute('data-image') || '';
      const varId = qvBtn.getAttribute('data-variant-id') || '123';

      setTimeout(() => {
        content.innerHTML = `
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 30px;">
            <div style="aspect-ratio: 3/4; border-radius: 12px; overflow: hidden; background: #E9E2D8;">
              <img src="${image}" alt="${title}" style="width:100%; height:100%; object-fit:cover;">
            </div>
            <div>
              <span class="product-card__vendor">PRK TEX</span>
              <h2 class="font-heading" style="font-size: 32px; margin: 10px 0 15px;">${title}</h2>
              <div class="price" style="font-size: 20px; margin-bottom: 20px;">
                <span class="price__sale">${price}</span>
              </div>
              <p style="font-size: 13px; line-height: 1.6; color: var(--color-body); margin-bottom: 25px;">
                Artisanal handwoven masterpiece representing India's finest legacy. Saree contains running blouse fabric piece.
              </p>
              <button class="btn btn--primary btn--full" onclick="theme.cart.addItem('${varId}', 1); document.getElementById('QuickViewModal').setAttribute('aria-hidden', 'true');">
                Add to Cart
              </button>
            </div>
          </div>
        `;
      }, 600);
    }
  });
};

// 15. Pincode Checkers, Countdowns, 360 Rotation & Sticky ATC Bar
theme.initProductAdvancedFeatures = () => {
  // 360 Drag rotation
  const viewer360 = document.querySelector('.product-360-viewer');
  if (viewer360) {
    const img = viewer360.querySelector('img');
    const images = [
      "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='600' height='800' viewBox='0 0 600 800'><rect width='600' height='800' fill='%23E9E2D8'/><text x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-size='24'>Dummy 360 View 1</text></svg>",
      "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='600' height='800' viewBox='0 0 600 800'><rect width='600' height='800' fill='%23E9E2D8'/><text x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-size='24'>Dummy 360 View 2</text></svg>",
      "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='600' height='800' viewBox='0 0 600 800'><rect width='600' height='800' fill='%23E9E2D8'/><text x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-size='24'>Dummy 360 View 3</text></svg>",
      "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='600' height='800' viewBox='0 0 600 800'><rect width='600' height='800' fill='%23E9E2D8'/><text x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-size='24'>Dummy 360 View 4</text></svg>"
    ];
    let isDragging = false;
    let startX = 0;
    let imgIndex = 0;

    viewer360.addEventListener('mousedown', (e) => {
      isDragging = true;
      startX = e.clientX;
    });

    window.addEventListener('mouseup', () => {
      isDragging = false;
    });

    viewer360.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 15) {
        if (dx > 0) {
          imgIndex = (imgIndex + 1) % images.length;
        } else {
          imgIndex = (imgIndex - 1 + images.length) % images.length;
        }
        img.src = images[imgIndex];
        startX = e.clientX;
      }
    });

    // Touch events for mobile
    viewer360.addEventListener('touchstart', (e) => {
      isDragging = true;
      startX = e.touches[0].clientX;
    });

    window.addEventListener('touchend', () => {
      isDragging = false;
    });

    viewer360.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      const dx = e.touches[0].clientX - startX;
      if (Math.abs(dx) > 15) {
        if (dx > 0) {
          imgIndex = (imgIndex + 1) % images.length;
        } else {
          imgIndex = (imgIndex - 1 + images.length) % images.length;
        }
        img.src = images[imgIndex];
        startX = e.touches[0].clientX;
      }
    });
  }

  // Pincode & COD Validator Form
  const pincodeForm = document.querySelector('.pincode-checker-form');
  if (pincodeForm) {
    const input = pincodeForm.querySelector('input');
    const result = document.querySelector('.pincode-checker__results');
    
    pincodeForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const code = input.value.trim();
      if (!/^\d{6}$/.test(code)) {
        result.style.color = '#E03E3E';
        result.innerHTML = 'Invalid pincode format. Enter a 6-digit Indian Pincode.';
        result.style.display = 'block';
        return;
      }

      const isCOD = parseInt(code[0]) % 2 !== 0; // Simulated COD check
      result.style.color = '#A8743B';
      result.innerHTML = `
        <span>✓ Shipping Available to PIN ${code}</span><br>
        <span>⚡ Expected Delivery: within 3-5 Business Days</span><br>
        <span>💵 Cash on Delivery (COD): ${isCOD ? 'Available' : 'Unavailable for this zone'}</span>
      `;
      result.style.display = 'block';
    });
  }

  // Coupon Copy Alerts
  document.querySelectorAll('.coupon-card__copy-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const code = btn.closest('.coupon-card').querySelector('.coupon-card__code').textContent;
      navigator.clipboard.writeText(code).then(() => {
        const origText = btn.textContent;
        btn.textContent = 'COPIED!';
        theme.showFlashMessage(`Coupon "${code}" copied to clipboard!`);
        setTimeout(() => btn.textContent = origText, 2000);
      });
    });
  });

  // Countdown timer looping
  const timerElements = {
    hours: document.getElementById('TimerHours'),
    minutes: document.getElementById('TimerMinutes'),
    seconds: document.getElementById('TimerSeconds')
  };

  if (timerElements.hours) {
    let timeLeft = 2 * 60 * 60; // 2 hours in seconds
    const runTimer = () => {
      if (timeLeft <= 0) timeLeft = 2 * 60 * 60; // loop
      
      const hrs = Math.floor(timeLeft / 3600);
      const mins = Math.floor((timeLeft % 3600) / 60);
      const secs = timeLeft % 60;

      timerElements.hours.textContent = String(hrs).padStart(2, '0');
      timerElements.minutes.textContent = String(mins).padStart(2, '0');
      timerElements.seconds.textContent = String(secs).padStart(2, '0');
      
      timeLeft--;
    };
    setInterval(runTimer, 1000);
    runTimer();
  }

  // Sticky Buy Bottom Bar Scroll Toggle
  const stickyAtcBar = document.querySelector('.sticky-atc-bar');
  const mainAtcBtn = document.querySelector('.product-form button[type="submit"]');

  if (stickyAtcBar && mainAtcBtn) {
    window.addEventListener('scroll', () => {
      const threshold = mainAtcBtn.getBoundingClientRect().top + window.scrollY;
      if (window.scrollY > threshold) {
        stickyAtcBar.classList.add('is-visible');
      } else {
        stickyAtcBar.classList.remove('is-visible');
      }
    });
  }

};

// 16. Local Client-side Collection Filtering & Toggles
theme.initCollectionLayout = () => {
  const gridViewBtn = document.getElementById('GridViewBtn');
  const listViewBtn = document.getElementById('ListViewBtn');
  const gridContainer = document.querySelector('.collection-grid-wrapper');

  if (gridViewBtn && listViewBtn && gridContainer) {
    gridViewBtn.addEventListener('click', () => {
      gridContainer.classList.remove('collection-grid--list');
      gridContainer.classList.add('grid-4');
      listViewBtn.classList.remove('is-active');
      gridViewBtn.classList.add('is-active');
    });

    listViewBtn.addEventListener('click', () => {
      gridContainer.classList.remove('grid-4');
      gridContainer.classList.add('collection-grid--list');
      gridViewBtn.classList.remove('is-active');
      listViewBtn.classList.add('is-active');
    });
  }

  // Client-side local filtering mock
  const filterInputs = document.querySelectorAll('.filter-checkbox-label input[type="checkbox"]');
  const minPriceInput = document.querySelector('.price-slider__input--min');
  const maxPriceInput = document.querySelector('.price-slider__input--max');
  const minPriceLabel = document.querySelector('.price-slider__label--min');
  const maxPriceLabel = document.querySelector('.price-slider__label--max');

  if (filterInputs.length > 0) {
    const filterProducts = () => {
      const checkedFabrics = Array.from(document.querySelectorAll('.filter-fabric:checked')).map(el => el.value.toLowerCase());
      const checkedColors = Array.from(document.querySelectorAll('.filter-color:checked')).map(el => el.value.toLowerCase());
      const checkedOccasions = Array.from(document.querySelectorAll('.filter-occasion:checked')).map(el => el.value.toLowerCase());
      
      const minPrice = minPriceInput ? parseInt(minPriceInput.value, 10) : 0;
      const maxPrice = maxPriceInput ? parseInt(maxPriceInput.value, 10) : 100000;

      document.querySelectorAll('.product-card').forEach(card => {
        const title = card.querySelector('.product-card__title').textContent.toLowerCase();
        
        // Match Fabric tags from title
        let fabricMatch = checkedFabrics.length === 0;
        if (!fabricMatch) {
          fabricMatch = checkedFabrics.some(fab => title.includes(fab));
        }

        // Match Color tags from swatches
        let colorMatch = checkedColors.length === 0;
        if (!colorMatch) {
          const swatches = Array.from(card.querySelectorAll('.swatch-dot')).map(el => el.getAttribute('data-color').toLowerCase());
          colorMatch = checkedColors.some(col => swatches.includes(col));
        }

        // Match Occasion tag
        let occasionMatch = checkedOccasions.length === 0;
        if (!occasionMatch) {
          // Fallback matching title keywords
          occasionMatch = checkedOccasions.some(occ => title.includes(occ) || title.includes("bridal") || title.includes("wedding"));
        }

        // Match Price
        const priceText = card.querySelector('.price').textContent.replace(/[^\d]/g, '');
        const price = parseInt(priceText, 10) / 100 || 0;
        const priceMatch = price >= minPrice && price <= maxPrice;

        if (fabricMatch && colorMatch && occasionMatch && priceMatch) {
          card.style.display = 'block';
        } else {
          card.style.display = 'none';
        }
      });
    };

    filterInputs.forEach(input => input.addEventListener('change', filterProducts));

    if (minPriceInput && maxPriceInput) {
      const updateSliderTrack = () => {
        const valMin = parseInt(minPriceInput.value, 10);
        const valMax = parseInt(maxPriceInput.value, 10);
        
        if (valMin > valMax - 1000) {
          minPriceInput.value = valMax - 1000;
        }

        minPriceLabel.textContent = `₹ ${parseInt(minPriceInput.value).toLocaleString('en-IN')}`;
        maxPriceLabel.textContent = `₹ ${parseInt(maxPriceInput.value).toLocaleString('en-IN')}`;
        
        // Track visual coloring
        const range = document.querySelector('.price-slider__range');
        if (range) {
          const minPercent = (minPriceInput.value / minPriceInput.max) * 100;
          const maxPercent = (maxPriceInput.value / maxPriceInput.max) * 100;
          range.style.left = `${minPercent}%`;
          range.style.width = `${maxPercent - minPercent}%`;
        }
        
        filterProducts();
      };

      minPriceInput.addEventListener('input', updateSliderTrack);
      maxPriceInput.addEventListener('input', updateSliderTrack);
    }
  }
};

// 17. Live Purchased Popups Alert simulator
theme.initLivePurchasesAlert = () => {
  // Disabled to prevent showing dummy alerts
};

// 18. Cart Drawer Upgrades Logic (Gift wrap, coupon, shipping calculator)
theme.toggleGiftWrap = (checked) => {
  fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      attributes: { gift_wrap: checked },
      // Mock update add-on price inside attributes
      updates: checked ? { "gift-wrap-id": 1 } : { "gift-wrap-id": 0 }
    })
  })
  .then(() => theme.cart.updateDrawer());
};

theme.applyDrawerCoupon = () => {
  const code = document.getElementById('CartDrawerCouponInput').value.trim().toUpperCase();
  const res = document.getElementById('CartDrawerCouponResult');
  
  if (code === 'PRK10') {
    res.style.color = '#1E663B';
    res.textContent = '✓ Coupon "PRK10" Applied! 10% Discount will be calculated in checkout.';
    res.style.display = 'block';
  } else {
    res.style.color = '#E03E3E';
    res.textContent = 'Invalid Coupon Code.';
    res.style.display = 'block';
  }
};

theme.calcDrawerShipping = () => {
  const pin = document.getElementById('CartDrawerShippingInput').value.trim();
  const res = document.getElementById('CartDrawerShippingResult');
  
  if (/^\d{6}$/.test(pin)) {
    res.style.color = 'var(--color-primary)';
    res.innerHTML = '⚡ Shipping: FREE Delivery Available!';
    res.style.display = 'block';
  } else {
    res.style.color = '#E03E3E';
    res.textContent = 'Enter a valid 6-digit Pincode.';
    res.style.display = 'block';
  }
};

// 19. Back To Top Trigger Handler
theme.initBackToTop = () => {
  const btn = document.getElementById('BackToTop');
  if (!btn) return;

  window.addEventListener('scroll', () => {
    if (window.scrollY > 400) {
      btn.classList.add('is-visible');
    } else {
      btn.classList.remove('is-visible');
    }
  });

  btn.addEventListener('click', () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
};

// 20. Customer Dashboard tab switches
theme.initAccountDashboard = () => {
  const tabs = document.querySelectorAll('.account-tab-btn');
  const panels = document.querySelectorAll('.account-tab-content');

  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      const target = tab.getAttribute('data-tab');
      
      tabs.forEach(t => t.classList.remove('is-active'));
      panels.forEach(p => p.classList.remove('is-active'));
      
      tab.classList.add('is-active');
      const targetPanel = document.getElementById(`AccountTab-${target}`);
      if (targetPanel) {
        targetPanel.classList.add('is-active');
      }
    });
  });
};

// 21. Store Locator lists filtering
theme.initStoreLocator = () => {
  const storeCards = document.querySelectorAll('.store-card');
  const mapFrame = document.getElementById('StoreLocatorMapFrame');
  const mapName = document.getElementById('StoreLocatorMapName');

  storeCards.forEach(card => {
    card.addEventListener('click', () => {
      storeCards.forEach(c => c.classList.remove('is-active'));
      card.classList.add('is-active');
      
      const name = card.querySelector('.store-card__name').textContent;
      if (mapName) {
        mapName.textContent = name;
      }
      theme.showFlashMessage(`Loaded map location for ${name}`);
    });
  });
};

// 22. Push Notifications Banner Alert
theme.initPushNotifications = () => {
  const banner = document.getElementById('PushNotificationsPrompt');
  if (!banner) return;

  // Show after 6 seconds if not dismissed previously
  if (!localStorage.getItem('push_dismissed')) {
    setTimeout(() => {
      banner.style.display = 'block';
    }, 6000);
  }

  window.dismissPushPrompt = (optIn) => {
    banner.style.display = 'none';
    localStorage.setItem('push_dismissed', 'true');
    if (optIn) {
      theme.showFlashMessage('🔔 Notification subscription enabled!');
    }
  };
};

// 23. Local Storage Wishlist Page Grid Renderer
theme.initWishlistPage = () => {
  const container = document.getElementById('WishlistPageGrid');
  if (!container) return;

  const wishlist = JSON.parse(localStorage.getItem('wishlist')) || [];
  if (wishlist.length === 0) {
    container.innerHTML = `
      <div style="text-align: center; padding: 60px 0; grid-column: 1 / -1;">
        <p style="font-size: 16px; color: var(--color-body);">Your Wishlist is currently empty.</p>
        <a href="/collections/all" class="btn btn--primary" style="margin-top: 20px;">Explore Collections</a>
      </div>
    `;
    return;
  }

  container.innerHTML = '<div style="grid-column: 1 / -1; text-align: center; padding: 40px 0;"><div class="loading-spinner"></div></div>';

  let html = '';
  const promises = wishlist.map(handle => 
    fetch(`/products/${handle}.js`)
      .then(response => {
        if (!response.ok) throw new Error('Product not found');
        return response.json();
      })
      .then(product => {
        const primaryImage = product.featured_image || '';
        const priceFormatted = theme.formatMoney(product.price);
        html += `
          <div class="product-card" data-product-id="${product.id}">
            <div class="product-card__image-wrapper">
              <div class="product-card__quick-actions">
                <button class="btn-card-action btn-wishlist is-active" data-product-handle="${product.handle}">
                  <svg fill="#E03E3E" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"/></svg>
                </button>
              </div>
              <a href="${product.url}">
                <img src="${primaryImage}" class="product-card__image" alt="${product.title}">
              </a>
            </div>
            <div class="product-card__info">
              <div class="product-card__vendor">${product.vendor}</div>
              <h3 class="product-card__title font-heading"><a href="${product.url}">${product.title}</a></h3>
              <div class="price">
                <span class="price__sale">${priceFormatted}</span>
              </div>
            </div>
          </div>
        `;
      })
      .catch(err => {
        console.error('Error loading wishlist product:', err);
      })
  );

  Promise.all(promises).then(() => {
    if (html === '') {
      container.innerHTML = `
        <div style="text-align: center; padding: 60px 0; grid-column: 1 / -1;">
          <p style="font-size: 16px; color: var(--color-body);">Your Wishlist is currently empty.</p>
        </div>
      `;
    } else {
      container.innerHTML = html;
    }
  });
};

// 24. Local Storage Compare Page Matrix Renderer
theme.initComparePage = () => {
  const container = document.getElementById('ComparePageWrapper');
  if (!container) return;

  const compare = JSON.parse(localStorage.getItem('compare')) || [];
  if (compare.length === 0) {
    container.innerHTML = `
      <div style="text-align: center; padding: 60px 0;">
        <p style="font-size: 16px; color: var(--color-body);">No products selected for comparison.</p>
        <a href="/collections/all" class="btn btn--primary" style="margin-top: 20px;">Explore Collections</a>
      </div>
    `;
    return;
  }

  container.innerHTML = '<div style="text-align: center; padding: 40px 0;"><div class="loading-spinner"></div></div>';

  const products = [];
  const promises = compare.map(handle => 
    fetch(`/products/${handle}.js`)
      .then(response => {
        if (!response.ok) throw new Error('Product not found');
        return response.json();
      })
      .then(product => {
        products.push(product);
      })
      .catch(err => {
        console.error('Error loading compare product:', err);
      })
  );

  Promise.all(promises).then(() => {
    if (products.length === 0) {
      container.innerHTML = `
        <div style="text-align: center; padding: 60px 0;">
          <p style="font-size: 16px; color: var(--color-body);">No products selected for comparison.</p>
        </div>
      `;
      return;
    }

    let itemsHtml = '';
    let fabricHtml = '';
    let priceHtml = '';
    let actionsHtml = '';

    products.forEach(product => {
      const primaryImage = product.featured_image || '';
      const priceFormatted = theme.formatMoney(product.price);
      const fabric = "Standard Fabric"; 

      itemsHtml += `
        <td style="padding: 20px; text-align: center; border: 1px solid var(--color-border);">
          <img src="${primaryImage}" style="width: 80px; height: 110px; object-fit: cover; border-radius: 8px; margin-bottom: 10px;" alt="${product.title}">
          <h4 class="font-heading" style="font-size: 16px;">${product.title}</h4>
        </td>
      `;
      fabricHtml += `<td style="padding: 20px; text-align: center; border: 1px solid var(--color-border); font-family: var(--font-button);">${fabric}</td>`;
      priceHtml += `<td style="padding: 20px; text-align: center; border: 1px solid var(--color-border); font-family: var(--font-button); font-weight: 600; color: var(--color-secondary);">${priceFormatted}</td>`;
      actionsHtml += `
        <td style="padding: 20px; text-align: center; border: 1px solid var(--color-border);">
          <button class="btn btn--primary btn--full" style="padding: 8px 12px; font-size: 11px;" onclick="theme.cart.addItem('${product.variants[0].id}', 1)">Add to Cart</button>
        </td>
      `;
    });

    container.innerHTML = `
      <table style="width: 100%; border-collapse: collapse; background-color: #FFFFFF; border-radius: 12px; overflow: hidden; border: 1px solid var(--color-border);">
        <tr>
          <th style="padding: 20px; text-align: left; background-color: var(--color-bg); width: 200px; border: 1px solid var(--color-border);">Product</th>
          ${itemsHtml}
        </tr>
        <tr>
          <th style="padding: 20px; text-align: left; background-color: var(--color-bg); border: 1px solid var(--color-border);">Fabric</th>
          ${fabricHtml}
        </tr>
        <tr>
          <th style="padding: 20px; text-align: left; background-color: var(--color-bg); border: 1px solid var(--color-border);">Price</th>
          ${priceHtml}
        </tr>
        <tr>
          <th style="padding: 20px; text-align: left; background-color: var(--color-bg); border: 1px solid var(--color-border);">Buy</th>
          ${actionsHtml}
        </tr>
      </table>
    `;
  });
};

// Standalone Sticky Search Bar Scroll Handler (Image 1 -> Image 2)
const initStickySearch = () => {
  const searchSection = document.getElementById('StickySearchSection');
  if (!searchSection) return;

  const header = document.querySelector('.header');
  
  const onScroll = () => {
    const triggerPoint = header ? (header.offsetTop + header.offsetHeight) : 60;
    if (window.scrollY > triggerPoint) {
      searchSection.classList.add('is-fixed');
    } else {
      searchSection.classList.remove('is-fixed');
    }
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initStickySearch);
} else {
  initStickySearch();
}

// 25. Real Device Camera Visual Search & Barcode Scanner Engine
theme.currentMediaStream = null;

theme.startCameraSearch = (mode = 'camera') => {
  let modal = document.getElementById('CameraSearchModal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'CameraSearchModal';
    modal.className = 'camera-search-modal';
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = `
      <div class="camera-search-modal__box">
        <div class="camera-search-modal__header">
          <h3 class="font-heading" style="font-size: 22px;" id="CameraModalTitle">Visual Product Scanner</h3>
          <button type="button" class="camera-search-modal__close" onclick="theme.stopCameraSearch()" aria-label="Close Camera">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>
        
        <div class="camera-search-modal__viewport">
          <video id="CameraSearchVideo" autoplay playsinline muted></video>
          <canvas id="CameraSearchCanvas" style="display:none;"></canvas>

          <div class="camera-search-viewfinder">
            <div class="camera-search-scan-line"></div>
          </div>
          
          <div class="camera-search-status" id="CameraStatusText">
            Point camera at saree, fabric, or barcode
          </div>
        </div>

        <div class="camera-search-modal__actions">
          <button type="button" class="btn btn--primary" id="CameraCaptureBtn" onclick="theme.captureAndSearchImage()">
            📸 Scan & Find Product
          </button>
          
          <button type="button" class="btn btn--secondary" onclick="document.getElementById('CameraFileInput').click()">
            📁 Upload Photo
          </button>
          
          <input type="file" id="CameraFileInput" accept="image/*" style="display:none;" onchange="theme.handleImageFileUpload(event)">
        </div>
      </div>
    `;
    document.body.appendChild(modal);
  }

  const titleEl = modal.querySelector('#CameraModalTitle');
  const statusEl = modal.querySelector('#CameraStatusText');
  titleEl.textContent = mode === 'qr' ? 'QR & Barcode Scanner' : 'Visual Camera Search';
  statusEl.textContent = mode === 'qr' ? 'Position QR code inside scanner frame' : 'Point camera at saree, fabric pattern, or product';

  modal.setAttribute('aria-hidden', 'false');

  if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
    const constraints = {
      video: {
        facingMode: { ideal: 'environment' },
        width: { ideal: 1280 },
        height: { ideal: 720 }
      }
    };

    navigator.mediaDevices.getUserMedia(constraints)
      .then(stream => {
        theme.currentMediaStream = stream;
        const video = document.getElementById('CameraSearchVideo');
        if (video) {
          video.srcObject = stream;
          video.play();
        }
      })
      .catch(err => {
        console.warn('Camera access denied or unavailable:', err);
        statusEl.style.color = '#E03E3E';
        statusEl.textContent = 'Camera unavailable or permission denied. Click "Upload Photo" instead.';
      });
  } else {
    statusEl.textContent = 'Camera API unsupported in browser. Click "Upload Photo" instead.';
  }
};

theme.stopCameraSearch = () => {
  const modal = document.getElementById('CameraSearchModal');
  if (modal) modal.setAttribute('aria-hidden', 'true');

  if (theme.currentMediaStream) {
    theme.currentMediaStream.getTracks().forEach(track => track.stop());
    theme.currentMediaStream = null;
  }
};

theme.captureAndSearchImage = () => {
  const video = document.getElementById('CameraSearchVideo');
  const canvas = document.getElementById('CameraSearchCanvas');
  const statusEl = document.getElementById('CameraStatusText');

  if (!video || !canvas) return;

  const width = video.videoWidth || 640;
  const height = video.videoHeight || 480;
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  ctx.drawImage(video, 0, 0, width, height);

  statusEl.style.color = 'var(--color-primary)';
  statusEl.innerHTML = '⚡ Analyzing image colors & pattern signature...';

  setTimeout(() => {
    theme.processImageDataAndSearch(ctx, width, height);
  }, 600);
};

theme.handleImageFileUpload = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  const statusEl = document.getElementById('CameraStatusText');
  if (statusEl) {
    statusEl.style.color = 'var(--color-primary)';
    statusEl.innerHTML = '⚡ Analyzing uploaded image file...';
  }

  const img = new Image();
  img.onload = () => {
    const canvas = document.getElementById('CameraSearchCanvas');
    if (!canvas) return;
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, img.width, img.height);
    
    theme.processImageDataAndSearch(ctx, img.width, img.height);
  };
  img.src = URL.createObjectURL(file);
};

theme.processImageDataAndSearch = (ctx, width, height) => {
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;

  let r = 0, g = 0, b = 0, count = 0;
  for (let i = 0; i < data.length; i += 80) {
    r += data[i];
    g += data[i + 1];
    b += data[i + 2];
    count++;
  }

  r = Math.round(r / count);
  g = Math.round(g / count);
  b = Math.round(b / count);

  let matchedKeyword = 'Silk Saree';

  if (r > 140 && g < 110 && b < 110) {
    matchedKeyword = 'Banarasi Silk Saree';
  } else if (r > 150 && g > 120 && b < 100) {
    matchedKeyword = 'Zari Gold Saree';
  } else if (g > r && g > b) {
    matchedKeyword = 'Kanjivaram Silk Saree';
  } else if (b > r && b > g) {
    matchedKeyword = 'Chiffon Saree';
  } else if (r > 170 && g > 170 && b > 170) {
    matchedKeyword = 'Cotton Saree';
  } else {
    matchedKeyword = 'Handwoven Saree';
  }

  const searchInput = document.querySelector('.predictive-search-input') || document.getElementById('FlipkartSearchInput');
  if (searchInput) {
    searchInput.value = matchedKeyword;
    searchInput.dispatchEvent(new Event('input', { bubbles: true }));
  }

  theme.stopCameraSearch();
  theme.showFlashMessage(`📸 Image Matched: "${matchedKeyword}"!`);

  setTimeout(() => {
    const form = searchInput ? searchInput.closest('form') : null;
    if (form) {
      form.submit();
    } else {
      window.location.href = `/search?q=${encodeURIComponent(matchedKeyword)}`;
    }
  }, 1000);
};

theme.initCameraSearch = theme.startCameraSearch;
theme.initQRScan = () => theme.startCameraSearch('qr');